import type { Job } from "../../../../../../../models";
import { ColumnContext } from "../ColumnContext";
import { CheckboxColumnViewModel, GroupCheckboxState, IndividualCheckboxChangeOptions } from "./checkboxColumn.types";
import { getGroupJobs, allJobsHaveFieldValue, someJobsHaveFieldValue } from "./checkboxColumn.helpers";

/**
 * Hook that provides the CheckboxColumn ViewModel
 * @param context - The column context containing state and helpers
 * @returns ViewModel with all business logic functions
 */
export function useCheckboxColumnViewModel(context: ColumnContext): CheckboxColumnViewModel {
	/**
	 * Get current checkbox value from job
	 * Uses row.original directly instead of O(n) jobs.find() lookup
	 */
	function getCurrentCheckboxValue(job: Job, fieldKey: keyof Job): boolean {
		return (job?.[fieldKey] as boolean) || false;
	}

	/**
	 * Calculate group checkbox state (checked/indeterminate)
	 */
	function calculateGroupCheckboxState(
		context: ColumnContext,
		groupValue: string,
		fieldKey: keyof Job,
		groupByField: keyof Job,
		groupStateKey?: string,
	): GroupCheckboxState {
		let isChecked = false;
		let isIndeterminate = false;

		if (groupStateKey === "readySelectionState") {
			isChecked = context.readySelectionState[groupValue] || false;
			const groupJobs = getGroupJobs(context.jobs, groupByField, groupValue);
			isIndeterminate = !isChecked && someJobsHaveFieldValue(groupJobs, fieldKey);
		}

		return { isChecked, isIndeterminate };
	}

	/**
	 * Handle individual checkbox change
	 */
	function handleIndividualCheckboxChange(
		context: ColumnContext,
		jobId: string,
		fieldKey: keyof Job,
		checked: boolean,
		groupValue: string,
		options?: IndividualCheckboxChangeOptions,
	): void {
		const { hasGroupUpdate, groupStateKey, groupByField } = options || {};

		// Use immutable pattern (same as handleGroupCheckboxChange)
		const newJobs = context.jobs.map((job) => (job.id === jobId ? { ...job, [fieldKey]: checked } : job));
		const updatedJob = newJobs.find((j) => j.id === jobId);
		if (!updatedJob) return;

		context.setJobs(newJobs);

		// Handle group state synchronization if needed
		if (hasGroupUpdate && groupStateKey && groupByField) {
			const groupJobs = getGroupJobs(newJobs, groupByField, groupValue);
			const allAreSelected = allJobsHaveFieldValue(groupJobs, fieldKey);

			// Update group state
			if (groupStateKey === "readySelectionState") {
				context.setReadySelectionState((prevState) => ({
					...prevState,
					[groupValue]: allAreSelected,
				}));
			}
		}

		context.validateJobs([updatedJob], { updateState: true, clearPrevious: false });
	}

	/**
	 * Handle group checkbox change
	 */
	function handleGroupCheckboxChange(
		context: ColumnContext,
		groupValue: string,
		fieldKey: keyof Job,
		checked: boolean,
		groupByField: keyof Job,
		groupStateKey?: string,
	): void {
		const newJobs = context.jobs.map((job) =>
			String(job[groupByField] ?? "") === groupValue ? { ...job, [fieldKey]: checked } : job,
		);
		context.setJobs(newJobs);

		// Update group state
		if (groupStateKey === "readySelectionState") {
			context.setReadySelectionState((prevState) => ({
				...prevState,
				[groupValue]: checked,
			}));
		}

		context.validateJobs(newJobs, { updateState: true, clearPrevious: false });
	}

	return {
		getCurrentCheckboxValue: (job, fieldKey) => getCurrentCheckboxValue(job, fieldKey),

		calculateGroupCheckboxState: (groupValue, fieldKey, groupByField, groupStateKey) =>
			calculateGroupCheckboxState(context, groupValue, fieldKey, groupByField, groupStateKey),

		handleIndividualCheckboxChange: (jobId, fieldKey, checked, groupValue, options) =>
			handleIndividualCheckboxChange(context, jobId, fieldKey, checked, groupValue, options),

		handleGroupCheckboxChange: (groupValue, fieldKey, checked, groupByField, groupStateKey) =>
			handleGroupCheckboxChange(context, groupValue, fieldKey, checked, groupByField, groupStateKey),
	};
}
